import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar } from 'lucide-react';
import { experiences } from '../../data/mock';

const TimelineCard = ({ exp, index, tightPull }) => {
  const isLeft = index % 2 === 0;
  const pullClass = index === 0 ? '' : tightPull ? 'lg:-mt-10' : 'lg:-mt-44';

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
      className={`relative flex ${isLeft ? 'lg:justify-start' : 'lg:justify-end'} mb-12 ${pullClass}`}
    >
      {/* Timeline dot */}
      <div className="absolute left-0 lg:left-1/2 w-4 h-4 rounded-full bg-[#00D4FF] -translate-x-1/2 mt-6 z-10 glow-cyan" />

      {/* Card */}
      <div className="ml-8 lg:ml-0 lg:w-[45%] glass-card glass-card-hover rounded-xl p-6">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {exp.tags.map((tag, i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded text-xs font-mono bg-[rgba(0,212,255,0.08)] text-[#00D4FF] border border-[rgba(0,212,255,0.2)]"
            >
              {tag}
            </span>
          ))}
        </div>

        <h3 className="font-display text-xl font-bold text-[#F0F4FF] mb-1">{exp.title}</h3>
        <p className="font-display text-lg text-[#00D4FF] mb-2">{exp.company}</p>

        <div className="flex flex-wrap gap-4 text-xs font-mono text-[#8892A4] mb-4">
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {exp.dates}
          </span>
          {exp.location && (
            <span className="flex items-center gap-1">
              <MapPin size={12} />
              {exp.location}
            </span>
          )}
        </div>

        {/* Metrics */}
        <div className="flex flex-wrap gap-2 mb-4">
          {exp.metrics.map((metric, i) => (
            <span
              key={i}
              className="px-3 py-1 rounded-lg text-xs font-mono bg-[rgba(57,255,20,0.06)] text-[#39FF14] border border-[rgba(57,255,20,0.15)]"
            >
              {metric}
            </span>
          ))}
        </div>

        {/* Bullets */}
        <ul className="space-y-2">
          {exp.bullets.map((bullet, i) => (
            <li
              key={i}
              className="text-sm text-[#C0C8D8] pl-4 relative"
            >
              <span className="absolute left-0 text-[#00D4FF] text-xs">&#9656;</span>
              {bullet}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const ExperienceSection = () => {
  return (
    <section id="experience" className="py-24 relative z-10">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="section-label block mb-4"
        >
          // 4+ YEARS SHIPPING
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-16"
        >
          Career Timeline
        </motion.h2>

        {/* Timeline */}
        <div className="relative">
          {/* Central line */}
          <div className="absolute left-0 lg:left-1/2 top-0 bottom-0 w-px -translate-x-1/2">
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: '100%' }}
              viewport={{ once: true }}
              transition={{ duration: 2, ease: 'easeInOut' }}
              className="w-full bg-gradient-to-b from-[#00D4FF] via-[#00D4FF] to-transparent"
            />
          </div>

          {/* Experience entries */}
          {experiences.map((exp, index) => (
            <TimelineCard
              key={index}
              exp={exp}
              index={index}
              tightPull={index > 0 && experiences[index - 1].bullets.length === 0}
            />
          ))}

        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
